import * as pdfjsLib from 'pdfjs-dist';

// Set worker source - using local worker from public folder
// This worker is copied from node_modules/pdfjs-dist/build/pdf.worker.min.mjs
// and always matches the installed package version
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/**
 * Load PDF and create document model
 * @param {string|ArrayBuffer} source - URL or data
 * @returns {Promise<Object>} DocumentModel with page metrics
 */
export async function loadPdfDocument(source) {
  try {
    // Load PDF
    const loadingTask = typeof source === 'string'
      ? pdfjsLib.getDocument({ url: source, disableFontFace: false })
      : pdfjsLib.getDocument({ data: source, disableFontFace: false });
    
    const pdfDocument = await loadingTask.promise;
    const numPages = pdfDocument.numPages;
    
    // Preload page metrics (dimensions and rotation)
    const pages = [];
    for (let i = 1; i <= numPages; i++) {
      const page = await pdfDocument.getPage(i);
      const viewport = page.getViewport({ scale: 1.0 });
      
      pages.push({
        pageNum: i,
        width: viewport.width,
        height: viewport.height,
        rotation: viewport.rotation || 0,
      });
    }
    
    return {
      pdfDocument,
      numPages,
      pages,
    };
  } catch (error) {
    console.error('Failed to load PDF:', error);
    throw error;
  }
}

/**
 * Get page metrics by page number (1-based)
 */
export function getPageMetrics(documentModel, pageNum) {
  if (!documentModel || !documentModel.pages) return null;
  return documentModel.pages[pageNum - 1]; // Convert to 0-based index
}

/**
 * Render a single PDF page to canvas
 */
export async function renderPageToCanvas(pdfDocument, pageNum, canvas, scale) {
  try {
    const page = await pdfDocument.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    
    // Get device pixel ratio for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    
    // Set canvas dimensions (accounting for DPR)
    canvas.width = viewport.width * dpr;
    canvas.height = viewport.height * dpr;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    
    // Render
    const renderContext = {
      canvasContext: ctx,
      viewport: viewport,
    };
    
    const renderTask = page.render(renderContext);
    await renderTask.promise;
    
    return { 
      width: viewport.width, 
      height: viewport.height,
      renderTask // Return render task so it can be cancelled
    };
  } catch (error) {
    // Ignore cancelled render errors
    if (error.name === 'RenderingCancelledException') {
      console.log(`Render cancelled for page ${pageNum}`);
      return null;
    }
    console.error(`Failed to render page ${pageNum}:`, error);
    throw error;
  }
}
